import { useEffect, useState } from 'react';
import { LineChart, Line, ResponsiveContainer, Tooltip, YAxis, XAxis } from 'recharts';

/**
 * Kleine Verlaufskurven je Gerät (Tagesverdichtung, Standard 30 Tage).
 *
 * Die Daten kommen aus `ingest_device_daily` — einem Datensatz pro Gerät und
 * Tag. Die Rohberichte selbst werden nach wenigen Tagen gelöscht; für einen
 * Monatsverlauf wären sie auch viel zu groß.
 */

interface Tag {
  day: string;
  reportsReceived: number;
  availabilityPercent: number;
  cpuTempAvgC: number | null;
  cpuTempMaxC: number | null;
  memPercentAvg: number | null;
  diskPercentMax: number | null;
  packetLossAvgPercent: number | null;
  internetLatencyAvgMs: number | null;
  storageWearPercent: number | null;
  storageUnsafeShutdowns: number | null;
  vpnConnectedPercent: number | null;
}

interface Props {
  deviceId: string;
  days?: number;
}

const tagKurz = (iso: string) =>
  new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });

function Kurve({
  titel,
  daten,
  feld,
  einheit,
  farbe,
  aktuell,
  hinweis,
  domain,
}: {
  titel: string;
  daten: Tag[];
  feld: keyof Tag;
  einheit: string;
  farbe: string;
  aktuell: string;
  hinweis?: string;
  domain?: [number, number];
}) {
  const punkte = daten
    .map((d) => ({ tag: tagKurz(d.day), wert: d[feld] as number | null }))
    .filter((p) => p.wert !== null);

  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs text-slate-500">{titel}</span>
        <span className="text-sm font-medium text-slate-800">{aktuell}</span>
      </div>
      <div className="h-16">
        {punkte.length >= 2 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={punkte} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
              <XAxis dataKey="tag" hide />
              <YAxis hide domain={domain ?? ['dataMin', 'dataMax']} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
                labelFormatter={(l) => `${l}`}
                formatter={(v) => [`${Math.round(Number(v) * 10) / 10}${einheit}`, titel]}
              />
              <Line
                type="monotone"
                dataKey="wert"
                stroke={farbe}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center text-xs text-slate-400">
            {hinweis ?? 'Noch keine Verlaufsdaten — die Verdichtung läuft nachts.'}
          </div>
        )}
      </div>
    </div>
  );
}

export function DeviceHistoryCharts({ deviceId, days = 30 }: Props) {
  const [daten, setDaten] = useState<Tag[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    const ac = new AbortController();
    setDaten(null);
    setFehler(null);
    fetch(`/api/admin/ingest-devices/${deviceId}/history?days=${days}`, {
      credentials: 'include',
      signal: ac.signal,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (aktiv) setDaten(json.history ?? []);
      })
      .catch((e) => {
        if (aktiv && e.name !== 'AbortError') setFehler('Verlauf konnte nicht geladen werden.');
      });
    return () => {
      aktiv = false;
      ac.abort();
    };
  }, [deviceId, days]);

  if (fehler) {
    return <div className="text-sm text-red-600">{fehler}</div>;
  }
  if (!daten) {
    return <div className="text-sm text-slate-400">Verlauf wird geladen …</div>;
  }
  if (daten.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 p-3 text-sm text-slate-500">
        Noch keine Verlaufsdaten. Die Tagesverdichtung läuft einmal nachts — ab morgen
        erscheinen hier die letzten {days} Tage.
      </div>
    );
  }

  const letzter = daten[daten.length - 1];
  const wert = (v: number | null | undefined, einheit: string, stellen = 0) =>
    v === null || v === undefined ? '–' : `${v.toFixed(stellen)}${einheit}`;

  return (
    <div className="grid grid-cols-3 gap-4">
      <Kurve
        titel={`Erreichbarkeit (${daten.length} Tage)`}
        daten={daten}
        feld="availabilityPercent"
        einheit="%"
        farbe="#16a34a"
        domain={[0, 100]}
        aktuell={wert(letzter.availabilityPercent, '%', 1)}
      />
      <Kurve
        titel="Temperatur (Tagesmittel)"
        daten={daten}
        feld="cpuTempAvgC"
        einheit="°C"
        farbe="#ea580c"
        aktuell={wert(letzter.cpuTempAvgC, '°C', 1)}
      />
      <Kurve
        titel="Verschleiß Datenträger"
        daten={daten}
        feld="storageWearPercent"
        einheit="%"
        farbe="#7c3aed"
        domain={[0, 100]}
        aktuell={wert(letzter.storageWearPercent, '%')}
        hinweis="Wird ab dem nächsten Tag erfasst."
      />
    </div>
  );
}
